import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import {
  hasUsedTripChatVoiceInput,
  isTripChatVoicePromptSuppressed,
  recordTripChatVoicePromptDismissed,
  subscribeToTripChatVoicePrompts,
} from 'storage/tripChatVoicePrompts'
import { trackVoicePromptEvent } from 'tripPlanner/utils/chatAnalytics'
import type { VoicePrompt } from '../voicePromptTypes'
import { useMicrophoneAvailability } from './useMicrophoneAvailability'

function getTrue() {
  return true
}

function getFalse() {
  return false
}

interface Options {
  voicePrompt: VoicePrompt | undefined
  canShow: boolean
  chatId: number | undefined
}

export function useVoicePrompt({ voicePrompt, canShow, chatId }: Options) {
  const isMicrophoneAvailable = useMicrophoneAvailability()
  const isSuppressed = useSyncExternalStore(subscribeToTripChatVoicePrompts, isTripChatVoicePromptSuppressed, getTrue)
  const hasUsedVoiceInput = useSyncExternalStore(subscribeToTripChatVoicePrompts, hasUsedTripChatVoiceInput, getFalse)
  const isPromoting = !!voicePrompt && isMicrophoneAvailable
  const promptKey = `${voicePrompt}:${chatId}`

  // "Try it now" only hides the prompt for this chat; a transcript is what snoozes it.
  const [acceptedPromptKey, setAcceptedPromptKey] = useState<string>()
  const [closingPromptKey, setClosingPromptKey] = useState<string>()
  const isClosing = closingPromptKey === promptKey
  const isOpen = isClosing ||
    (isPromoting && !isSuppressed && acceptedPromptKey !== promptKey && canShow)

  const trackedPromptKeyRef = useRef<string>(undefined)
  useEffect(() => {
    if (isOpen && voicePrompt && trackedPromptKeyRef.current !== promptKey) {
      trackedPromptKeyRef.current = promptKey
      trackVoicePromptEvent('shown', voicePrompt, chatId)
    }
  }, [chatId, isOpen, promptKey, voicePrompt])

  // Closing prompts stay mounted for their exit animation; a second click must not count again.
  const accept = useCallback(() => {
    if (!voicePrompt || isClosing) return
    setAcceptedPromptKey(promptKey)
    setClosingPromptKey(promptKey)
    trackVoicePromptEvent('accepted', voicePrompt, chatId)
  }, [chatId, isClosing, promptKey, voicePrompt])

  const dismiss = useCallback(() => {
    if (!voicePrompt || isClosing) return
    recordTripChatVoicePromptDismissed()
    setClosingPromptKey(promptKey)
    trackVoicePromptEvent('dismissed', voicePrompt, chatId)
  }, [chatId, isClosing, promptKey, voicePrompt])

  const finishClosing = useCallback(() => {
    setClosingPromptKey(undefined)
  }, [])

  return {
    isOpen,
    isClosing,
    isMicrophoneEmphasised: isPromoting && !hasUsedVoiceInput,
    accept,
    dismiss,
    finishClosing,
  }
}
